import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanDeactivateFn,
  Router,
  RouterStateSnapshot,
  Routes
} from '@angular/router';
import { DocumentBaseComponent } from '../application/components/forms/document-base/document-base.component';
import { lastValueFrom, Subject } from 'rxjs';
import { ICustomDialog, IUser } from '@interfaces/nebula';
import { closeSaveDialog } from '@factories/helpers';
import { TemplateService } from '@services/template.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { LoginService } from '@services/login.service';
import { getCurrentUser } from '@factories/user.factory';
import { SharedataService } from '@services/sharedata.service';
import { UserService } from '@services/user.service';
import { RoleService } from '@services/roles.service';
import { ClientNotificationToastService } from '@services/client-notifications/client-notification-toast.service';
import { DocumentService } from '@services/document.service';
import { SidePanelService } from '@services/side-panel.service';

export function canDeactivateDocumentGuard(): CanDeactivateFn<DocumentBaseComponent> {
  return component => {
    if (!component?.currentDocument) {
      return Promise.resolve(true);
    }

    const closeSubject$ = new Subject<boolean>();
    const documentService: DocumentService = inject(DocumentService);
    const documentLabel = documentService.getTypeLabel(component.currentDocument);
    const create = (): ICustomDialog<boolean> => {
      return closeSaveDialog(
        `${documentLabel} schließen`,
        'Es gibt ungespeicherte Änderungen auf dem Dokument. Was möchten Sie tun?',
        closeSubject$,
        (subject$: Subject<boolean>) => {
          void lastValueFrom(component.saveChanges(subject$));
        }
      );
    };

    return lastValueFrom(component.isDocumentLayoutHasChanges()).then(
      hasChanges => {
        if (hasChanges) {
          const dialog = create();
          dialog.show();
          return lastValueFrom(closeSubject$);
        }
        return true;
      },
      () => {
        const dialog = create();
        dialog.show();
        return lastValueFrom(closeSubject$);
      }
    );
  };
}

export function executeTemplateGuard(): CanActivateFn {
  return (snapshot: ActivatedRouteSnapshot) => {
    const templateService: TemplateService = inject(TemplateService);
    return templateService
      .templatePermissions(snapshot.params.templateid as string)
      .pipe(map(permissions => !!permissions.find(x => x === 'execute')));
  };
}

export function loginRouteGuard(): CanActivateFn {
  return async (_: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const loginService: LoginService = inject(LoginService);
    const router: Router = inject(Router);
    const url = state.url;

    const user = getCurrentUser();
    if (user) {
      const isPersistent = await lastValueFrom(
        loginService.isAuthenticated().pipe(
          filter(isAuthenticated => isAuthenticated),
          switchMap(() => loginService.info(false))
        )
      ).then(
        () => true,
        () => false
      );

      if (isPersistent) {
        return true;
      }
    }

    // Store the attempted URL for redirecting
    loginService.redirectUrl = url;

    // Navigate to the login page with extras only in case server is alive
    const isHealthy = await lastValueFrom(loginService.healthCheck());
    if (isHealthy) {
      await router.navigate(['/start']);
    }

    return false;
  };
}

export function isPersistanceGuard(): CanActivateFn {
  return async (snapshot: ActivatedRouteSnapshot) => {
    const loginService = inject(LoginService);
    const router = inject(Router);
    const sharedData = inject(SharedataService);
    const userService = inject(UserService);
    const roleService = inject(RoleService);
    const notificationToast = inject(ClientNotificationToastService);

    let user: IUser = null;
    const checkPersistent = (): Promise<boolean> => {
      return lastValueFrom(
        loginService.isAuthenticated().pipe(
          filter(isAuthenticated => isAuthenticated),
          switchMap(() => loginService.info(false))
        )
      ).then(
        infoUser => {
          user = infoUser;
          return true;
        },
        () => false
      );
    };

    let isPersistent = await checkPersistent();
    if (snapshot.queryParamMap.get('logout') === 'true' && isPersistent) {
      const autoLogout = sharedData.autoLogout();
      await lastValueFrom(loginService.logOut(autoLogout));
      userService.onLogout();
      roleService.onLogout();
      sharedData.startedOnce.set(false);
      sharedData.isUserNotificationClosed.set(false);
      notificationToast.hideAllToasts();
    }

    isPersistent = await checkPersistent();
    // Der Passwortwechsel-Zwang gilt nur für den Passwort-Login, nicht nach einer SAML-Anmeldung (UP-11)
    const isSaml2Login = snapshot.queryParamMap.get('login') === 'saml2';
    if (isPersistent && (isSaml2Login || !user?.isPasswordChangeRequired)) {
      await router.navigate(['/application/home']);
      return false;
    }

    return true;
  };
}

/**
 * Fragt beim Verlassen einer Route die Schließabfrage des aktiven Seitenpanel-Inhalts; schließt das
 * Panel nicht selbst (das geschieht bei `NavigationEnd`). Ohne aktiven Inhalt oder ohne
 * Schließabfrage ist die Navigation erlaubt.
 */
export function canDeactivateSidePanelGuard(): CanDeactivateFn<unknown> {
  return () => inject(SidePanelService).canDeactivate();
}

/**
 * Hängt `canDeactivateSidePanelGuard` rekursiv an alle Blatt-Routen (Routen ohne `children`), jeweils
 * ans Ende von `canDeactivate`. Hinweis: Der Router startet alle Guards eines Arrays gleichzeitig; die
 * Reihenfolge bestimmt nur, wessen Ablehnung Vorrang hat. Redirect-Routen und Routen mit
 * `loadChildren` bleiben unverändert; nachgeladene Routen erhalten den Guard nicht.
 */
export function withSidePanelGuard(routes: Routes): Routes {
  const guard = canDeactivateSidePanelGuard();
  const apply = (list: Routes): Routes =>
    list.map(route => {
      if (route.children) {
        return { ...route, children: apply(route.children) };
      }
      if (route.redirectTo !== undefined || route.loadChildren) {
        return route;
      }
      return { ...route, canDeactivate: [...(route.canDeactivate ?? []), guard] };
    });
  return apply(routes);
}
